import type { ThemeName } from "@octopusdeploy/design-system-components";
import { useApplyGlobalThemeEffect, Theme as DesignSystemTheme, MuiThemeApplier } from "@octopusdeploy/design-system-components";
import { logger } from "@octopusdeploy/logging";
import * as React from "react";
import { useThemePaletteType } from "~/components/Theme/useThemePaletteType";
import type { OctopusTheme } from "./Themes";
import { DarkTheme, LightTheme } from "./Themes";
type LegacyThemeName = "light" | "dark";
type ThemeProps = {
    palette: LegacyThemeName;
    children: React.ReactNode;
    updateFontFamilyOnBody: boolean;
    selectedFont: string;
};
function Theme({ palette, children, selectedFont, updateFontFamilyOnBody }: ThemeProps) {
    const themeName: ThemeName = palette;
    const legacyTheme = palette === "light" ? LightTheme : DarkTheme;
    useApplyGlobalThemeEffect(themeName, selectedFont, updateFontFamilyOnBody, legacyTheme);
    React.useEffect(() => {
        logger.info("Theme palette set to {themeName}", { themeName });
    }, [themeName]);
    return (<DesignSystemTheme themeName={themeName} customFontOverride={selectedFont} isFullHeight={true}>
            <MuiThemeApplier themeName={palette}>{children}</MuiThemeApplier>
        </DesignSystemTheme>);
}
function getLegacyTheme(themeName: LegacyThemeName): OctopusTheme {
    return themeName === "light" ? LightTheme : DarkTheme;
}
function withTheme(render: (theme: OctopusTheme) => React.ReactElement | null | undefined): React.ReactElement {
    return <UsingTheme>{(theme) => render(theme)}</UsingTheme>;
}
type UsingThemeProps = {
    children: (theme: OctopusTheme) => React.ReactNode;
};
function UsingTheme({ children }: UsingThemeProps) {
    const theme = useOctopusTheme();
    return <>{children(theme)}</>;
}
function useOctopusTheme(): OctopusTheme {
    const palette = useThemePaletteType();
    return getLegacyTheme(palette);
}
export default Theme;
export { Theme, withTheme, useOctopusTheme };
export type { OctopusTheme };
